import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/contexts/AuthContext';

/**
 * Voice-note recorder button.
 * - Click once to start recording, click again to stop + upload + transcribe.
 * - Uses browser MediaRecorder API, sends the blob to /api/ai/voice-note/{prospectId}.
 * - Calls onTranscribed(text) on success so the parent can refresh activities.
 */
export default function VoiceNoteButton({ prospectId, onTranscribed, size = 'sm', className = '' }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Micro non supporté par ce navigateur");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      // Feature-detect supported mime type (iOS Safari needs mp4/m4a)
      const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      const mimeType = preferred.find(t => window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || '';
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const actualMime = mr.mimeType || 'audio/webm';
      const ext = actualMime.includes('mp4') ? 'm4a' : actualMime.includes('ogg') ? 'ogg' : 'webm';
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: actualMime });
        if (blob.size < 1000) { toast.error("Enregistrement trop court"); return; }
        if (blob.size > 25 * 1024 * 1024) { toast.error("Audio trop volumineux (max 25 Mo)"); return; }
        const fd = new FormData();
        fd.append('file', blob, `voice_note.${ext}`);
        setUploading(true);
        try {
          const { data } = await api.post(`/ai/voice-note/${prospectId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          toast.success(`Note vocale transcrite (${data.length} car.)`);
          onTranscribed?.(data.text);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Échec transcription");
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      toast.info("🎤 Enregistrement… cliquez pour arrêter");
    } catch (e) {
      toast.error("Autorisation micro refusée");
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const handleClick = recording ? stop : start;
  const label = uploading ? 'Transcription…' : recording ? 'Stop' : 'Note vocale';
  const icon = uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />;

  return (
    <Button
      type="button"
      size={size}
      variant={recording ? 'destructive' : 'outline'}
      onClick={handleClick}
      disabled={uploading}
      data-testid="voice-note-btn"
      className={`gap-1.5 ${recording ? 'animate-pulse' : ''} ${className}`}
    >
      {icon}
      {label}
    </Button>
  );
}
